import { useState } from "react";
import { ArrowRight, ArrowLeft, Upload, Info } from "lucide-react";
import { publicDocsUrl } from "@/lib/docs";

interface ConfigData {
  ambiente: string;
  erp: string;
  volumen: string;
  tieneP12: boolean;
  p12File: File | null;
  p12Password: string;
}

interface Step3Props {
  data: ConfigData;
  onChange: (data: ConfigData) => void;
  onNext: () => void;
  onBack: () => void;
}

const ERP_OPTIONS = ["Odoo", "SAP", "Dynamics 365", "NetSuite", "Oracle ERP", "Propio / Personalizado", "Otro"];
const VOLUMENES = [
  "Menos de 100 e-CF/mes",
  "100 – 500 e-CF/mes",
  "500 – 5,000 e-CF/mes",
  "5,000 – 25,000 e-CF/mes",
  "Más de 25,000 e-CF/mes",
];

const AMBIENTES = [
  { value: "testecf", label: "TesteCF", desc: "Pruebas iniciales en el ambiente TesteCF oficial de la DGII" },
  { value: "certecf", label: "CerteCF", desc: "Ambiente de Certificación oficial para aprobación de DGII" },
  { value: "ecf", label: "eCF (Producción)", desc: "Emisión real con validez fiscal 24/7 (Requiere certificación previa)" },
];

export default function Step3Config({ data, onChange, onNext, onBack }: Step3Props) {
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!data.ambiente) e.ambiente = "Selecciona un ambiente";
    if (!data.erp) e.erp = "Selecciona tu ERP";
    if (!data.volumen) e.volumen = "Selecciona el volumen estimado";
    if (data.tieneP12 && !data.p12File) e.p12 = "Sube el archivo P12";
    if (data.tieneP12 && !data.p12Password) e.p12Password = "Ingresa la contraseña del P12";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    onChange({ ...data, p12File: file });
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">Configuración fiscal</h2>
        <p className="text-sm text-slate-500">Configura el ambiente de emisión y tu certificado digital.</p>
      </div>

      {/* Ambiente */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Ambiente inicial</label>
        <div className="space-y-2">
          {AMBIENTES.map((amb) => (
            <label key={amb.value} className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
              <input
                type="radio"
                name="ambiente"
                value={amb.value}
                checked={data.ambiente === amb.value}
                onChange={() => onChange({ ...data, ambiente: amb.value })}
                className="accent-blue-800"
              />
              <div>
                <p className="text-sm font-semibold text-slate-800">{amb.label}</p>
                <p className="text-xs text-slate-500">{amb.desc}</p>
              </div>
            </label>
          ))}
        </div>
        {errors.ambiente && <p className="text-xs text-red-500 mt-1">{errors.ambiente}</p>}
      </div>

      {/* ERP */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">ERP de origen</label>
        <select
          value={data.erp}
          onChange={(e) => onChange({ ...data, erp: e.target.value })}
          className={`w-full border rounded-xl px-4 py-2.5 text-sm outline-none transition-colors cursor-pointer ${
            errors.erp ? "border-red-300" : "border-slate-200 focus:border-blue-700"
          }`}
        >
          <option value="">Selecciona tu ERP...</option>
          {ERP_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {errors.erp && <p className="text-xs text-red-500 mt-1">{errors.erp}</p>}
      </div>

      {/* Volumen */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Volumen mensual estimado</label>
        <select
          value={data.volumen}
          onChange={(e) => onChange({ ...data, volumen: e.target.value })}
          className={`w-full border rounded-xl px-4 py-2.5 text-sm outline-none transition-colors cursor-pointer ${
            errors.volumen ? "border-red-300" : "border-slate-200 focus:border-blue-700"
          }`}
        >
          <option value="">Seleccionar volumen...</option>
          {VOLUMENES.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        {errors.volumen && <p className="text-xs text-red-500 mt-1">{errors.volumen}</p>}
      </div>

      {/* P12 */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Certificado digital P12</label>
        <div className="flex gap-3">
          <label className="flex items-center gap-2 p-3 flex-1 border rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
            <input
              type="radio"
              name="p12"
              checked={data.tieneP12 === true}
              onChange={() => onChange({ ...data, tieneP12: true })}
              className="accent-blue-800"
            />
            <span className="text-sm font-medium text-slate-700">Tengo mi P12</span>
          </label>
          <label className="flex items-center gap-2 p-3 flex-1 border rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
            <input
              type="radio"
              name="p12"
              checked={data.tieneP12 === false}
              onChange={() => onChange({ ...data, tieneP12: false })}
              className="accent-blue-800"
            />
            <span className="text-sm font-medium text-slate-700">No tengo P12</span>
          </label>
        </div>

        {data.tieneP12 ? (
          <div className="mt-3 space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Archivo .p12</label>
              <label className={`flex items-center gap-3 border-2 border-dashed rounded-xl px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                errors.p12 ? "border-red-300" : "border-slate-200"
              }`}>
                <Upload size={16} className="text-slate-400" />
                <span className="text-sm text-slate-500">
                  {data.p12File ? data.p12File.name : "Haz clic para subir el archivo .p12"}
                </span>
                <input type="file" accept=".p12,.pfx" onChange={handleFile} className="hidden" />
              </label>
              {errors.p12 && <p className="text-xs text-red-500 mt-1">{errors.p12}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Contraseña del P12</label>
              <input
                type="password"
                value={data.p12Password}
                onChange={(e) => onChange({ ...data, p12Password: e.target.value })}
                placeholder="Contraseña del certificado"
                className={`w-full border rounded-xl px-4 py-2.5 text-sm outline-none transition-colors ${
                  errors.p12Password ? "border-red-300" : "border-slate-200 focus:border-blue-700"
                }`}
              />
              {errors.p12Password && <p className="text-xs text-red-500 mt-1">{errors.p12Password}</p>}
            </div>
          </div>
        ) : (
          <div className="mt-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
            <div className="flex items-start gap-2">
              <Info size={15} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800 mb-1">Obtén tu certificado P12</p>
                <p className="text-xs text-amber-700 leading-relaxed">
                  Para emitir e-CF en producción necesitas un certificado digital emitido por la DGII.
                  Puedes cargarlo después del registro desde el panel de empresa.{" "}
                  <a href={publicDocsUrl("#cargar-p12")} className="underline font-medium cursor-pointer">Ver guía paso a paso</a>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <button
          onClick={onBack}
          className="flex items-center gap-2 border border-slate-200 text-slate-600 font-medium px-5 py-3 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <ArrowLeft size={16} />
          Atrás
        </button>
        <button
          onClick={() => validate() && onNext()}
          className="flex-1 flex items-center justify-center gap-2 bg-blue-800 text-white font-semibold py-3 rounded-xl hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          Continuar
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}